import React, { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import Swal from 'sweetalert2';
import jwt_decode from 'jwt-decode';
import { InventoryOrdersTable } from './components/InventoryOrdersTable';
import { AddEditOrderModalWrapper } from './Modals/AddEditOrderModalWrapper';
import { DeleteConfirmationModal } from './Modals/DeleteConfirmationModal';
import { createInventoryOrder, updateInventoryOrder, deleteInventoryOrder, getPropertyList, getInventoryItems, getInventoryOrders } from './utils/api';
import { getAllRMVendors } from '../RMVendors/Helpers/Api';
import { PropertySelector } from './components/PropertySelector';
import { getValidationSchema, getInitialValues } from './Helpers/Config';
import { OrderStatus } from './constants/statuses';
import './styles/styles.css';

const InventoryOrdersForTechnicalStaff = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [properties, setProperties] = useState([]);
  const [rmVendors, setRMVendors] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [vendorCategories, setVendorCategories] = useState([]);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const user = getUserFromToken();
    if (!user) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(user?.role || '', user?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch properties.',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, getUserFromToken]);

  const fetchRMVendors = useCallback(async (propertyId) => {
    if (!propertyId) {
      setRMVendors([]);
      setVendorCategories([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getAllRMVendors(propertyId);
      setRMVendors(result?.data || []);
      setVendorCategories(result?.categories || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch vendors.',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const fetchInventoryItems = useCallback(async (propertyId, buildingName) => {
    if (!propertyId || !buildingName) {
      setInventoryItems([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await getInventoryItems(propertyId, buildingName);
      setInventoryItems(response?.items || []);
    } catch (error) {
      setInventoryItems([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to fetch inventory.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const fetchOrders = useCallback(async (propertyId, buildingName) => {
    if (!propertyId || !buildingName) {
      setOrders([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await getInventoryOrders(propertyId, buildingName);
      setOrders(response?.orders || []);
    } catch (error) {
      setOrders([]);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to fetch orders.'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const formik = useFormik({
    initialValues: getInitialValues('add', null, {}),
    validationSchema: getValidationSchema('add'),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const user = getUserFromToken();
        if (!user) throw new Error('User not authenticated');

        const formattedOrderedItems = (values.orderedItems || []).map(item => ({
          requestsSupported: item.requestsSupported || null,
          item_id: item.item_id,
          requestedQuantity: Number(item.requestedQuantity),
          price: Number(item.price),
          jobName: item.jobName.trim(),
        }));

        const payload = {
          propertyId: values.propertyId,
          buildingName: values.buildingName,
          vendorId: values.vendorId,
          orderedItems: formattedOrderedItems,
          status: values.status || OrderStatus.DRAFT,
          comments: values.comments?.trim() || '', // Orderer comments
          deliveryComments: values.deliveryComments?.trim() || '', // Delivery comments
        };

        const result = await createInventoryOrder(payload);
        if (!result.success) throw new Error(result.message || 'Failed to create inventory order');

        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Inventory order created successfully!'
        });

        await fetchOrders(values.propertyId, values.buildingName);
        resetForm({
          values: getInitialValues('add', null, {
            propertyId: values.propertyId,
            buildingName: values.buildingName
          })
        });
        setModal('');
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to create inventory order.'
        });
      } finally {
        setSubmitting(false);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  });

  const openAddModal = useCallback(() => {
    formik.setValues(getInitialValues('add', null, {
      propertyId: formik.values.propertyId,
      buildingName: formik.values.buildingName
    }));
    setModal('add');
  }, [formik]);

  const openViewModal = useCallback((order) => {
    setSelectedOrder(order);
    formik.setValues(getInitialValues('view', order, {}));
    setModal('view');
  }, [formik]);

  const openEditModal = useCallback((order) => {
    setSelectedOrder(order);
    formik.setValues(getInitialValues('edit', order, {
      comments: order.comments || '', // Initialize orderer comments
      deliveryComments: order.delivery?.comments || '' // Initialize delivery comments
    }));
    setModal('edit');
  }, [formik]);

  const openDeleteModal = useCallback((order) => {
    setSelectedOrder(order);
    setModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    setModal('');
  }, []);

  const handleEditSubmit = useCallback(async (values, { setSubmitting }) => {
    try {
      if (!selectedOrder?._id) throw new Error('Missing order ID');

      dispatch({ type: 'SET_LOADING', payload: true });

      const formattedOrderedItems = (values.orderedItems || []).map(item => ({
        requestsSupported: item.requestsSupported || null,
        item_id: item.item_id,
        requestedQuantity: Number(item.requestedQuantity),
        price: Number(item.price),
        jobName: item.jobName.trim(),
      }));

      const payload = {
        propertyId: values.propertyId,
        buildingName: values.buildingName,
        vendorId: values.vendorId,
        orderedItems: formattedOrderedItems,
        status: values.status,
        comments: values.comments?.trim() || '', // Orderer comments
        deliveryComments: values.deliveryComments?.trim() || '', // Delivery comments
        delivery: {
          status: values.delivery?.status,
          comments: values.deliveryComments?.trim() || values.delivery?.comments || '',
        },
      };

      const result = await updateInventoryOrder(selectedOrder._id, payload);
      if (!result.success) throw new Error(result.message || 'Failed to update order');

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Order updated successfully'
      });

      await fetchOrders(values.propertyId, values.buildingName);
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update order'
      });
    } finally {
      setSubmitting(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedOrder, fetchOrders, closeModal]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      if (!selectedOrder?._id) throw new Error('No order selected');

      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await deleteInventoryOrder(selectedOrder._id);
      if (!result.success) throw new Error(result.message || 'Failed to delete order');

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Order deleted successfully'
      });

      await fetchOrders(formik.values.propertyId, formik.values.buildingName);
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete order'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedOrder, fetchOrders, formik.values, closeModal]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    const { propertyId, buildingName } = formik.values;
    if (propertyId) {
      fetchRMVendors(propertyId);
    } else {
      setRMVendors([]);
      setVendorCategories([]);
    }
    if (propertyId && buildingName) {
      fetchInventoryItems(propertyId, buildingName);
      fetchOrders(propertyId, buildingName);
    } else {
      setInventoryItems([]);
      setOrders([]);
    }
  }, [formik.values.propertyId, formik.values.buildingName, fetchRMVendors, fetchInventoryItems, fetchOrders]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Inventory Orders</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Inventory"
            breadcrumbItem="Orders Lead"
          />
          <PropertySelector
            formik={formik}
            propertyList={properties}
            onAddClick={openAddModal}
          />
          <InventoryOrdersTable
            orders={orders}
            onViewDetails={openViewModal}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
            onSuccess={() => fetchOrders(formik.values.propertyId, formik.values.buildingName)}

          />
          <AddEditOrderModalWrapper
            mode={modal}
            open={modal === 'add' || modal === 'view' || modal === 'edit'}
            onClose={closeModal}
            onAdd={formik.handleSubmit}
            onEdit={handleEditSubmit}
            formik={formik}
            rmVendors={rmVendors}
            vendorItems={inventoryItems}
            vendorCategories={vendorCategories}
          />
          <DeleteConfirmationModal
            open={modal === 'delete'}
            onClose={closeModal}
            onDelete={handleDeleteConfirm}
            order={selectedOrder}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export { InventoryOrdersForTechnicalStaff };